import React, { useState } from 'react';
import { ArrowRight, Check, ChevronRight, GitBranch, Save } from 'lucide-react';

import { isPlyFieldVisible, sizeSuffix } from '@/components/DynamicFieldRenderer';
import { JobCardAttachments } from '@/components/attachments';
import ProcessOrderPicker from '@/components/phone/ProcessOrderPicker';
import { FieldRenderer } from '@/components/phone/FieldControls';
import {
  Card, PhonePage, PrimaryButton, Spinner, StatusPill, TabRow, TopBar,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';
import { iconFor } from './ProductTypePicker';

/**
 * Filling in a job card, on the admin's phone.
 *
 * The desktop screen is three panes side by side — spec sheet, process picker,
 * notes — and at a handset's width three panes stacked are three postage
 * stamps. The supervisor already has a phone answer to the same questions, so
 * this is that answer with the admin's extra question on top: the supervisor's
 * card arrives with its product type decided, the admin's does not.
 *
 * So, top to bottom: the order line's facts, the type as a grid of tappable
 * tiles, the pipeline the picks resolve to, then three tabs — the processes
 * picked with a thumb (the supervisor's list-and-add picker rather than the
 * desktop's number-per-row grid), the specification as labelled 48px fields,
 * and the notes and files. The state lives on the page; this only draws it, so
 * the desktop and the phone save exactly the same thing.
 */

const TABS = [
  { key: 'processes', label: 'Processes', testID: 'fill-tab-processes' },
  { key: 'details', label: 'Details', testID: 'fill-tab-details' },
  { key: 'notes', label: 'Notes', testID: 'fill-tab-notes' },
];

const isBlank = (value) =>
  value === undefined || value === null || value === '' ||
  (Array.isArray(value) && value.length === 0);

function Fact({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-[0.5px]" style={{ color: Colors.text.secondary }}>
        {label}
      </p>
      <p className="mt-0.5 truncate text-[14px] font-semibold capitalize" style={{ color: Colors.text.primary }}>
        {value || '—'}
      </p>
    </div>
  );
}

function SpecField({ field, value, orderType, autoFilled, onChange, onPlyChange }) {
  const type = field.type || 'text';
  const label = (field.label?.replace(/\s*\([^)]*\)\s*$/, '') || field.name)
    + (field.name === 'size' ? sizeSuffix(orderType) : '');
  const wide = ['textarea', 'checkbox_group'].includes(type);

  const write = (next) => {
    if (field.name === 'ply' && onPlyChange) { onPlyChange(next); return; }
    // a number field stores a number, the same as the desktop grid does
    onChange(field.name, type === 'number' && next !== '' ? Number(next) : next);
  };

  return (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="mb-1.5 flex items-center gap-1">
        <span className="min-w-0 flex-1 truncate text-[11px] font-medium" style={{ color: Colors.text.secondary }}>
          {label}
          {field.required && <span style={{ color: Colors.status.error }}> *</span>}
        </span>
        {autoFilled && !isBlank(value) && (
          <span
            className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium"
            style={{ backgroundColor: Colors.accent.wash, color: Colors.accent.DEFAULT }}
          >
            from order
          </span>
        )}
      </div>
      <FieldRenderer field={field} value={value} onChange={write} />
    </div>
  );
}

export default function PhoneFill({
  jobCard, info, started, pipelineLocked, stagesPassed, showFinancials,
  configs, config, orderType, onPickType,
  rules, fieldsByName, fieldSections, fields, filledCount,
  specs, updateSpec, onPlyChange,
  processOrder, setProcessOrder, pipeline, picked,
  saving, onSave, onBack, onOpenProcessTable,
}) {
  const [tab, setTab] = useState('processes');
  const autoFilled = jobCard.auto_filled_fields || [];

  const saveButton = (
    <button
      type="button"
      data-testid="save-job-card-btn"
      onClick={onSave}
      disabled={saving || !orderType}
      className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
      style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
    >
      {saving
        ? <Spinner size="sm" className="!border-white !border-b-transparent" />
        : <><Check className="h-4 w-4" /> Save</>}
    </button>
  );

  return (
    <PhonePage testID="job-card-fill-page">
      <TopBar
        eyebrow="FILL JOB CARD"
        title={jobCard.item_name || 'Job Card'}
        subtitle={`${jobCard.order_display_id ? `Order ${jobCard.order_display_id} · ` : ''}${info.customer_name || 'Standalone'}`}
        onBack={onBack}
        actions={saveButton}
      />

      <div className="flex-1 px-4 pb-10">
        {/* the order line this card is for — settled when the order was booked */}
        <Card className="mt-3">
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <Fact label="Quantity" value={info.quantity?.toLocaleString()} />
            {showFinancials && (
              <Fact label="Rate" value={info.item_value ? `₹${info.item_value.toLocaleString()}` : null} />
            )}
            <Fact label="Delivery" value={info.expected_delivery_date?.slice(0, 10)} />
            <Fact label="Priority" value={info.priority} />
            <Fact label="Card filled" value={`${jobCard.completion_percentage || 0}%`} />
          </div>
          <div className="mt-3 flex items-center justify-between gap-2">
            {started ? <StatusPill label="Work started" tone="caution" /> : <span />}
            <button
              type="button"
              onClick={onOpenProcessTable}
              className="flex h-9 items-center gap-1 text-[12px] font-semibold"
              style={{ color: Colors.accent.DEFAULT }}
            >
              Process table <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </Card>

        {/* what is being made — everything below is this answer's consequence */}
        <Card className="mt-3" title="Order type" aside={started ? 'locked — work has started' : undefined}>
          <div className="grid grid-cols-2 gap-2" data-testid="order-type-picker">
            {configs.map((item) => {
              const Icon = iconFor(item);
              const on = orderType === item.code;
              return (
                <button
                  key={item.code}
                  type="button"
                  disabled={started && !on}
                  aria-pressed={on}
                  onClick={() => onPickType(item.code)}
                  data-testid={`order-type-${item.code}`}
                  className="flex h-12 items-center gap-2 rounded-[10px] border px-3 text-left text-[13px] disabled:opacity-40"
                  style={{
                    backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
                    borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
                    color: on ? Colors.text.inverse : Colors.text.primary,
                    fontWeight: on ? 700 : 500,
                  }}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.name}</span>
                </button>
              );
            })}
          </div>
        </Card>

        {!config ? (
          <p className="py-10 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
            Pick an order type to see the processes it can run and its specification.
          </p>
        ) : (
          <>
            <Card className="mt-3">
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4" style={{ color: Colors.accent.DEFAULT }} />
                <h2 className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>Pipeline preview</h2>
              </div>
              {pipeline.length > 0 ? (
                <>
                  <div
                    data-testid="pipeline-preview"
                    className="-mx-4 mt-2 flex items-center gap-1 overflow-x-auto px-4 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  >
                    {pipeline.map((stage, idx) => (
                      <React.Fragment key={stage.stage_key || idx}>
                        <span
                          className="flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium"
                          style={{
                            backgroundColor: Colors.background.secondary,
                            borderColor: Colors.border.default,
                            color: Colors.text.secondary,
                          }}
                        >
                          <span
                            className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] tabular-nums"
                            style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
                          >
                            {idx + 1}
                          </span>
                          {stage.stage_name}
                        </span>
                        {idx < pipeline.length - 1 && (
                          <ChevronRight className="h-3.5 w-3.5 shrink-0" style={{ color: Colors.text.tertiary }} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px]" style={{ color: Colors.text.tertiary }}>
                    {pipeline.length} stage{pipeline.length === 1 ? '' : 's'}
                    {picked > 0 ? ' · picked by hand' : ' · routed off the specifications'} · updates on save
                  </p>
                </>
              ) : (
                <p className="mt-2 text-[12px]" style={{ color: Colors.text.tertiary }}>
                  Nothing numbered yet. Packing and Dispatch are added at the end automatically.
                </p>
              )}
            </Card>

            <div className="mt-3">
              <TabRow tabs={TABS} active={tab} onSelect={setTab} />
            </div>

            {tab === 'processes' && (pipelineLocked ? (
              <Card className="mt-3" testID="pipeline-locked">
                <p className="text-[13px] font-semibold" style={{ color: Colors.text.primary }}>Process list locked</p>
                <p className="mt-1 text-[12px] leading-relaxed" style={{ color: Colors.text.secondary }}>
                  This job has already passed {stagesPassed} stages, so it keeps the process
                  list it ran on. Move it back to the stage the change starts from, or raise a
                  fresh job card for the work that is left. The specification can still be edited.
                </p>
              </Card>
            ) : (
              <ProcessOrderPicker
                rules={rules}
                processOrder={processOrder}
                onChange={setProcessOrder}
                specs={specs}
                onSpecChange={updateSpec}
                fieldsByName={fieldsByName}
              />
            ))}

            {tab === 'details' && (
              fieldSections.length === 0 ? (
                <p className="py-10 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
                  No fields for this product type.
                </p>
              ) : (
                <>
                  {fieldSections.map(([sectionName, sectionFields]) => (
                    <Card key={sectionName} className="mt-3" title={sectionName} testID={`section-${sectionName}`}>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
                        {sectionFields.filter((field) => isPlyFieldVisible(field, specs)).map((field) => (
                          <SpecField
                            key={field.name}
                            field={field}
                            value={specs[field.name]}
                            orderType={orderType}
                            autoFilled={autoFilled.includes(field.name)}
                            onChange={updateSpec}
                            onPlyChange={onPlyChange}
                          />
                        ))}
                      </div>
                    </Card>
                  ))}
                  <p className="mt-3 text-center text-[11px]" style={{ color: Colors.text.tertiary }}>
                    {filledCount} of {fields.length} fields filled
                  </p>
                </>
              )
            )}

            {tab === 'notes' && (
              <Card className="mt-3" title="Notes & Files">
                <textarea
                  value={specs.remarks || ''}
                  onChange={(e) => updateSpec('remarks', e.target.value)}
                  rows={3}
                  aria-label="Notes"
                  placeholder="Notes for the floor on this job…"
                  className="min-h-[80px] w-full resize-y rounded-[10px] border px-3 py-2.5 text-[14px] outline-none"
                  style={{
                    backgroundColor: Colors.background.primary,
                    borderColor: Colors.border.default,
                    color: Colors.text.primary,
                  }}
                />
                <div className="mt-3">
                  <JobCardAttachments jobCardId={jobCard.id} bare />
                </div>
              </Card>
            )}

            {/* the same button as the bar, at the end of a long form */}
            <PrimaryButton
              className="mt-5"
              onClick={onSave}
              disabled={!orderType}
              busy={saving}
              testID="save-job-card-bottom"
            >
              <Save className="h-4 w-4" /> Save job card
            </PrimaryButton>
          </>
        )}
      </div>
    </PhonePage>
  );
}
